import React, { useState } from 'react';
export type BuddyMood = 'idle' | 'listening' | 'thinking' | 'speaking' | 'celebrating' | 'calm';
let buddyInstanceCounter = 0;

/**
 * "Sunny" — the dhp.voice health buddy.
 * A warm animated sun-blob that breathes, blinks, listens, thinks,
 * speaks (for narration) and celebrates (after tracking actions).
 * Mock of the planned Rive character — enough to design its presence.
 */
export function HealthBuddy({
  size = 56,
  mood = 'idle',
  className = ''
}: {
  size?: number;
  mood?: BuddyMood;
  className?: string;
}) {
  const [uid] = useState(() => `buddy${++buddyInstanceCounter}`);
  const face = '#5B4225';
  const isCelebrating = mood === 'celebrating';
  const isListening = mood === 'listening';
  const isThinking = mood === 'thinking';
  const isSpeaking = mood === 'speaking';
  /* calm — the emergency-guidance mood: slow, steady breathing, soft
     reassuring expression. The buddy doesn't panic, so you don't. */
  const isCalm = mood === 'calm';
  return <div className={`relative inline-block ${className}`} style={{
    width: size,
    height: size
  }} data-id="element-113">
      {/* listening ripples */}
      {isListening && <>
          <span className="absolute inset-0 rounded-full bg-teal-soft dhp-ring" data-id="element-114" />
          <span className="absolute inset-0 rounded-full bg-teal-soft dhp-ring" style={{
        animationDelay: '0.55s'
      }} data-id="element-115" />
        </>}

      <svg viewBox="0 0 100 100" width={size} height={size} className={`relative block ${isCelebrating ? 'dhp-wiggle' : 'dhp-breathe'}`} style={{
      filter: 'drop-shadow(0 3px 10px rgba(239, 161, 92, 0.35))',
      /* calm mood breathes slowly and steadily — same rhythm, half the pace */
      ...(isCalm ? {
        animationDuration: '7.5s'
      } : {})
    }} data-id="element-116">
        <defs data-id="element-117">
          <radialGradient id={`${uid}-body`} cx="38%" cy="30%" r="85%" data-id="element-118">
            <stop offset="0%" stopColor="#FFEAA8" data-id="element-119" />
            <stop offset="55%" stopColor="#F8C96A" data-id="element-120" />
            <stop offset="100%" stopColor="#EFA15C" data-id="element-121" />
          </radialGradient>
        </defs>

        {/* sun rays — only when celebrating */}
        {isCelebrating && <g stroke="#F2B14E" strokeWidth="3.5" strokeLinecap="round" className="dhp-spin-slow" style={{
        transformOrigin: '50px 50px'
      }} data-id="element-122">
            <line x1="50" y1="2" x2="50" y2="9" data-id="element-123" />
            <line x1="50" y1="91" x2="50" y2="98" data-id="element-124" />
            <line x1="2" y1="50" x2="9" y2="50" data-id="element-125" />
            <line x1="91" y1="50" x2="98" y2="50" data-id="element-126" />
            <line x1="16" y1="16" x2="21" y2="21" data-id="element-127" />
            <line x1="79" y1="79" x2="84" y2="84" data-id="element-128" />
            <line x1="84" y1="16" x2="79" y2="21" data-id="element-129" />
            <line x1="21" y1="79" x2="16" y2="84" data-id="element-130" />
          </g>}

        {/* body */}
        <circle cx="50" cy="50" r="37" fill={`url(#${uid}-body)`} data-id="element-131" />

        {/* cheeks */}
        <ellipse cx="33" cy="58" rx="6.5" ry="4.5" fill="#F09A72" opacity="0.6" data-id="element-132" />
        <ellipse cx="67" cy="58" rx="6.5" ry="4.5" fill="#F09A72" opacity="0.6" data-id="element-133" />

        {/* eyes */}
        {isCelebrating ? <g stroke={face} strokeWidth="3.5" strokeLinecap="round" fill="none" data-id="element-134">
            <path d="M32 47 q6 -8 12 0" data-id="element-135" />
            <path d="M56 47 q6 -8 12 0" data-id="element-136" />
          </g> : isThinking ? <g fill={face} data-id="element-137">
            <circle cx="38" cy="42" r="3.2" className="buddy-eye" data-id="element-138" />
            <circle cx="62" cy="42" r="3.2" className="buddy-eye" data-id="element-139" />
          </g> : isCalm ? (/* soft, restful eyes — steady and present */
      <g stroke={face} strokeWidth="3.2" strokeLinecap="round" fill="none" data-id="element-140">
            <path d="M33 46 q5 4 10 0" data-id="element-141" />
            <path d="M57 46 q5 4 10 0" data-id="element-142" />
          </g>) : <g fill={face} data-id="element-143">
            <circle cx="38" cy="46" r={isListening ? 4.2 : 3.6} className="buddy-eye" data-id="element-144" />
            <circle cx="62" cy="46" r={isListening ? 4.2 : 3.6} className="buddy-eye" data-id="element-145" />
          </g>}

        {/* mouth */}
        {isSpeaking ? <ellipse cx="50" cy="62" rx="7" ry="6" fill={face} className="buddy-mouth-talk" data-id="element-146" /> : isListening ? <circle cx="50" cy="63" r="4" fill={face} data-id="element-147" /> : isCelebrating ? <path d="M37 57 Q50 75 63 57 Q50 64 37 57z" fill={face} data-id="element-148" /> : isThinking ? <path d="M44 64 q6 3 12 0" stroke={face} strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-149" /> : isCalm ? <path d="M42 60 Q50 67 58 60" stroke={face} strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-150" /> : <path d="M40 59 Q50 69 60 59" stroke={face} strokeWidth="3.2" strokeLinecap="round" fill="none" data-id="element-151" />}
      </svg>
    </div>;
}